import React from "react";
import { motion } from "framer-motion";
import ComparisonCell from "./ComparisonCell";

/**
 * Comparison Table — 3-column head-to-head comparison
 * (Feature / Left / Right) with the right column highlighted in orange.
 *
 * Props:
 *  - testId
 *  - eyebrow (default "Head to Head")
 *  - heading (ReactNode)
 *  - subtitle
 *  - leftColumnHeader: string  (e.g., "Conventional Tower Hoist")
 *  - rightColumnHeader: string  (e.g., "LPMH Series")
 *  - rows: { feature: string, left: true|false|string, right: true|false|string }[]
 *  - footerNote?: string
 *  - background?: "cream" | "cream-light"  (default "cream-light")
 */
export default function ComparisonTable({
  testId,
  eyebrow = "Head to Head",
  heading,
  subtitle,
  leftColumnHeader,
  rightColumnHeader,
  rows = [],
  footerNote,
  background = "cream-light",
}) {
  const bgClass =
    background === "cream" ? "bg-liftile-cream" : "bg-liftile-cream-light";

  return (
    <section
      data-testid={testId}
      className={`relative ${bgClass} py-24 md:py-32 text-liftile-matte-black overflow-hidden`}
    >
      <div className="container-x">
        <motion.div
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.65 }}
          className="max-w-3xl mx-auto text-center mb-14 md:mb-16"
        >
          <div className="heading-eyebrow mb-3">{eyebrow}</div>
          <h2 className="display-2">{heading}</h2>
          {subtitle && (
            <p className="font-body text-base md:text-lg text-liftile-matte-black/70 mt-6 leading-relaxed max-w-2xl mx-auto">
              {subtitle}
            </p>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.7 }}
          className="max-w-4xl mx-auto rounded-3xl overflow-hidden border border-liftile-matte-black/[0.08] bg-white shadow-[0_20px_60px_-25px_rgba(0,0,0,0.2)]"
        >
          <div className="overflow-x-auto">
            <table
              data-testid={`${testId}-table`}
              className="w-full min-w-[640px] border-collapse"
            >
              <thead className="sticky top-0 z-10">
                <tr className="bg-liftile-matte-black">
                  <th className="text-left font-heading text-[11px] md:text-xs uppercase tracking-[0.18em] text-liftile-cream/80 font-semibold px-5 md:px-7 py-4">
                    Feature
                  </th>
                  <th className="text-center font-heading text-[11px] md:text-xs uppercase tracking-[0.18em] text-liftile-cream/80 font-semibold px-5 md:px-7 py-4 border-l border-white/15">
                    {leftColumnHeader}
                  </th>
                  <th className="text-center font-heading text-[11px] md:text-xs uppercase tracking-[0.18em] text-white font-semibold px-5 md:px-7 py-4 border-l border-white/15 bg-liftile-orange">
                    {rightColumnHeader}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, ri) => (
                  <tr
                    key={row.feature}
                    className={`border-b border-liftile-matte-black/[0.06] transition-colors hover:bg-liftile-orange/[0.04] ${
                      ri % 2 === 0
                        ? "bg-liftile-cream-light/40"
                        : "bg-transparent"
                    }`}
                  >
                    <td className="font-heading text-[11px] md:text-[12px] uppercase tracking-[0.15em] text-liftile-matte-black/85 font-semibold px-5 md:px-7 py-4 md:py-5 align-middle">
                      {row.feature}
                    </td>
                    <ComparisonCell value={row.left} />
                    <ComparisonCell value={row.right} highlight />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        {footerNote && (
          <p className="font-body italic text-xs md:text-sm text-liftile-matte-black/55 text-center mt-6 max-w-2xl mx-auto">
            {footerNote}
          </p>
        )}
      </div>
    </section>
  );
}
